<script setup>
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useWorksStore } from '@/stores/work.js';
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import { useServerTable } from '@/composables/useServerTable.js';
import { fancyDate } from '@/filters/fancyDate.js';


const props = defineProps({
  search: { type: String, default: '' },
  // статус: 'ACTIVE' | 'ARCHIVED' | др. при необходимости
  status: { type: String, default: undefined },
  // опциональные фильтры
  contract: { type: [Number, String], default: undefined },
  customer: { type: [Number, String], default: undefined },
  facility: { type: [Number, String], default: undefined },
  responsible: { type: [Number, String], default: undefined },
});

const emit = defineEmits(['initial-count-loaded']);
const initialLoadEmitted = ref(false);


const worksStore = useWorksStore();
const router = useRouter();
const {
  data: works,
  totalRecords,
  loading,
  rowsPerPage,
  currentPage,
  sortField,
  sortOrder,
  setParams,
  load,
  setSearch,
  setExtra,
  onPageChange,
  onSort,
  firstIndex
} = useServerTable({
  initialRows: 10,
  debounceMs: 500,
  storageKey: 'table.works.pageSize',
  loadPage: async ({ page, rows, search, ...extra }) => {
    return await worksStore.loadWorksList({
      page,
      page_size: rows,
      search,
      status: extra.status,
      contract: extra.contract,
      customer: extra.customer,
      facility: extra.facility,
      ordering: extra.ordering,
      responsible: extra.responsible
    });
  }
});

const goToWork = async (event) => {
  if (event.data && event.data.id) {
    await router.push(`/works/${event.data.id}`);
  }
};

watch(
    () => [props.search, props.status, props.contract, props.customer, props.facility, props.responsible],
    (newVals, oldVals) => {
      const isInitialLoad = oldVals === undefined;
      const [search, status, contract, customer, facility, responsible] = newVals || [];

      if (!isInitialLoad) {
        const [prevSearch, prevStatus, prevContract, prevCustomer, prevFacility, prevResponsible] = oldVals || [];
        if (search === prevSearch && status === prevStatus && contract === prevContract && customer === prevCustomer && facility === prevFacility && responsible === prevResponsible) {
          return;
        }
      }

      setParams({
        search: (search || '').trim(),
        extra: { status, contract, customer, facility, responsible },
        resetPage: true,
        immediate: isInitialLoad,
      });
    },
    { immediate: true }
);

// Эмитим count после первой загрузки
watch(totalRecords, (count) => {
  if (!initialLoadEmitted.value && count !== null) {
    initialLoadEmitted.value = true;
    emit('initial-count-loaded', { count, status: props.status });
  }
});


</script>

<template>
  <DataTable :value="works"
             stripedRows
             lazy
             paginator
             scrollable
             scrollHeight="flex"
             :rows="rowsPerPage"
             :rowsPerPageOptions="[10, 20, 30]"
             :totalRecords="totalRecords"
             :loading="loading"
             :first="firstIndex()"
             :sortField="sortField"
             :sortOrder="sortOrder"
             @page="onPageChange"
             @sort="onSort"
             @row-click="goToWork"
             class="cursor-pointer text-sm h-full"
  >
    <Column
        field="name"
        header="Название"
        sortable
        :headerClass="'w-1/2 lg:w-[30%]'"
        :bodyClass="'w-1/2 lg:w-[30%]'"
    >
      <template #body="{ data }">
        {{ data.name || data.item_name }}
      </template>
    </Column>

    <Column
        field="contract_number"
        header="Договор"
        sortable
        style="width: 20%"
        :headerClass="'hidden lg:table-cell'"
        :bodyClass="'hidden lg:table-cell'"
    >
      <template #body="{ data }">
        <span v-if="data.contract_number">
          № {{ data.contract_number }}<span v-if="data.contract_date"> от {{ fancyDate(data.contract_date, 'short') }}</span>
        </span>
      </template>
    </Column>

    <Column
        field="facility_short_name"
        header="Объект"
        sortable
        :headerClass="'w-1/2 lg:w-[35%]'"
        :bodyClass="'w-1/2 lg:w-[35%]'"
    >
      <template #body="{ data }">
        {{ data.facility_short_name }}
      </template>
    </Column>

    <Column
        field="primary_responsible_full_name"
        header="Ответственные"
        sortable
        style="width: 15%"
        :headerClass="'hidden lg:table-cell'"
        :bodyClass="'hidden lg:table-cell'"
    >
      <template #body="{ data }">
          {{ data.primary_responsible_full_name }}
      </template>
    </Column>

  </DataTable>
</template>


<style scoped />
